import {IOrderTaxRate} from "../autoorder/auto-order-models";
import {IProductImage} from "../product/product-models";
import {ShippingMethod} from "../../checkout/models/shipping/shipping-method";
import {CustomerAddress} from "../../checkout/models/customer/customer-address";
import {ITwCharity} from "../../checkout/models/invoice/invoice";
import {Warehouse} from "../../checkout/models/warehouse/warehouse";

export interface IOrderHistoryView {
    id: number;
    customerId: number;
    orderDate: string;
    status: string;
    source: string;
    products: IOrderHistoryViewProduct[];
    packslips: IOrderViewPackslip[];
    shippingMethod: ShippingMethod;
    address: CustomerAddress;
    warehouse: Warehouse;
    exclusiveTotalPrice: number;
    total: number;
    totalPrice: number;
    baseFreight: number;
    totalFreight: number;
    taxCost: number;
    taxExempt: boolean;
    totalTaxes: number;
    totalDuty: number;
    totalHandling: number;
    totalVolume: number;
    currencyCode: string;
    autoOrderDiscount: number;
    totalIoReward: number;
    bpsReference: IOrderViewBPSReference;
    charity: ITwCharity;
    nfr?: boolean;
    taxRates?: IOrderTaxRate[];
    vouchersPayments?: IOrderViewPayment[];
    nonVouchersPayments?: IOrderViewPayment[];
    shippingCost: number;
    shippingCostExclusive: number;
    shippingTax: number;
    shippingTaxInclusive: boolean;
    taxInclusive: boolean;
}

export interface IOrderHistoryViewProduct {
    id: number;
    usanaId: string;
    mhpId?: number;
    quantity: number;
    price?: number;
    totalPrice?: number;
    discount?: number;
    volume?: number;
    totalVolume?: number;
    name?: string;
    productImages?: IProductImage[];
    canAddToCart?: boolean;
    // a pack started from a prebuilt pack: that pack, set only where it has a pack PDP - links go there instead of the builder
    sourcePrebuiltPackId?: number;
}

export interface ILoadedOrderViews {
	tag: string;
	orderViews: IOrderHistoryView[];
}

export interface IOrderHistoryDateRange {
	display: string;
	endDate: string;
	startDate: string;
	tag: string;
}

export interface IOrderViewPackslip {
	packslipId: number;
	orderId: number;
	description?: string;
	packages: IOrderViewPackage[];
	shipDate?: string;
}

export interface IOrderViewPackage {
	packageId: number;
	trackingNumber?: string;
	trackingLink?: string;
}

export interface IOrderViewBPSReference {
	bpsNumber?: number;
    cashPSAccountNumber?: string;
}

export interface IOrderViewPayment {
    paymentId: number;
    paymentMethodType: string;
    paymentMethodTypeDisplay?: string;
    status: string;
    amount: number;
    amountDisplay?: string;
    isVoucher: boolean;
    isCancellable?: boolean;
    // settled portion of this payment — what it already covers on the order
    approvedShippingAmount?: number;
}

export interface IOrderHistoryViewProductDisplayAttribute {
    name: string;
    isCurrency: boolean;
}

// Identifies which step of the cancel-then-submit flow failed (mirrors the cart-gateway OrderPaymentStep enum).
export enum OrderPaymentStep {
    CANCEL = 'CANCEL',
    SUBMIT = 'SUBMIT'
}

// Outcome of quoting a repayment (mirrors payment-service's OrderRepaymentStatus).
export enum OrderRepaymentStatus {
    READY_TO_CHARGE = 'READY_TO_CHARGE',
    ALREADY_PAID = 'ALREADY_PAID',
    NOT_CANCELABLE = 'NOT_CANCELABLE',
    CANCEL_FAILED = 'CANCEL_FAILED'
}

// What the order still owes, decided by payment-service. The client never recomputes these figures.
export interface IOrderRepaymentQuote {
    status: OrderRepaymentStatus;
    orderTotal: number;
    settledAmount: number;
    remainingBalance: number;
    retainedPaymentIds?: number[];
    livePaymentIds?: number[];
    blockingPaymentId?: number;
}

// Result of submitting a new payment to complete a pending order (mirrors the cart-gateway OrderPaymentResult type).
export interface IOrderPaymentResult {
    success: boolean;
    failedStep?: OrderPaymentStep;
    message?: string;
    orderPayment?: IOrderViewPayment;
}
